/**
 * Book Lookup Service
 * Uses Open Library API to fetch book details from ISBN
 * Free API, no key required
 */

export interface BookDetails {
  isbn: string
  title: string
  author: string
  authors?: string[]
  publisher?: string
  publishDate?: string
  description?: string
  pageCount?: number
  coverImage?: string
  subjects?: string[]
  language?: string
}

interface OpenLibrarySearchResult {
  numFound: number
  docs: Array<{
    key: string
    title: string
    author_name?: string[]
    publisher?: string[]
    first_publish_year?: number
    isbn?: string[]
    cover_i?: number
    subject?: string[]
    language?: string[]
  }>
}

/**
 * Lookup book details by ISBN using Open Library API
 */
export async function lookupBookByISBN(isbn: string): Promise<BookDetails | null> {
  // Clean ISBN - remove dashes and spaces
  const cleanISBN = isbn.replace(/[-\s]/g, '')
  
  try {
    // Try Open Library ISBN API first
    const response = await fetch(
      `https://openlibrary.org/api/books?bibkeys=ISBN:${cleanISBN}&jscmd=data&format=json`
    )
    
    if (!response.ok) {
      throw new Error('Failed to fetch from Open Library')
    }
    
    const data = await response.json()
    const bookData = data[`ISBN:${cleanISBN}`]
    
    if (bookData) {
      return {
        isbn: cleanISBN,
        title: bookData.title || 'Unknown Title',
        author: bookData.authors?.[0]?.name || 'Unknown Author',
        authors: bookData.authors?.map((a: any) => a.name) || [],
        publisher: bookData.publishers?.[0]?.name || undefined,
        publishDate: bookData.publish_date || undefined,
        description: typeof bookData.excerpts?.[0]?.text === 'string' 
          ? bookData.excerpts[0].text 
          : undefined,
        pageCount: bookData.number_of_pages || undefined,
        coverImage: bookData.cover?.medium || bookData.cover?.small || undefined,
        subjects: bookData.subjects?.map((s: any) => s.name).slice(0, 5) || [],
      }
    }
    
    // Fallback to search API if ISBN API doesn't return results
    return await searchBookByISBN(cleanISBN)
    
  } catch (error) {
    console.error('Open Library ISBN lookup failed:', error)
    
    // Try search as fallback
    try {
      return await searchBookByISBN(cleanISBN)
    } catch (searchError) {
      console.error('Search fallback also failed:', searchError)
      return null
    }
  }
}

/**
 * Search for book by ISBN using Open Library Search API
 */
async function searchBookByISBN(isbn: string): Promise<BookDetails | null> {
  try {
    const response = await fetch(
      `https://openlibrary.org/search.json?isbn=${isbn}&limit=1`
    )
    
    if (!response.ok) {
      throw new Error('Search API request failed')
    }
    
    const data: OpenLibrarySearchResult = await response.json()
    
    if (data.numFound > 0 && data.docs[0]) {
      const book = data.docs[0]
      return {
        isbn,
        title: book.title || 'Unknown Title',
        author: book.author_name?.[0] || 'Unknown Author',
        authors: book.author_name || [],
        publisher: book.publisher?.[0] || undefined,
        publishDate: book.first_publish_year?.toString() || undefined,
        coverImage: book.cover_i 
          ? `https://covers.openlibrary.org/b/id/${book.cover_i}-M.jpg`
          : undefined,
        subjects: book.subject?.slice(0, 5) || [],
        language: book.language?.[0] || undefined,
      }
    }
    
    return null
  } catch (error) {
    console.error('Search API failed:', error)
    return null
  }
}

/**
 * Search books by title or author
 */
export async function searchBooks(query: string, limit: number = 10): Promise<BookDetails[]> {
  try {
    const response = await fetch(
      `https://openlibrary.org/search.json?q=${encodeURIComponent(query)}&limit=${limit}`
    )
    
    if (!response.ok) {
      throw new Error('Search request failed')
    }
    
    const data: OpenLibrarySearchResult = await response.json()
    
    return data.docs.map(book => ({
      isbn: book.isbn?.[0] || '',
      title: book.title || 'Unknown Title',
      author: book.author_name?.[0] || 'Unknown Author',
      authors: book.author_name || [],
      publisher: book.publisher?.[0] || undefined,
      publishDate: book.first_publish_year?.toString() || undefined,
      coverImage: book.cover_i 
        ? `https://covers.openlibrary.org/b/id/${book.cover_i}-M.jpg`
        : undefined,
      subjects: book.subject?.slice(0, 5) || [],
    }))
  } catch (error) {
    console.error('Book search failed:', error)
    return []
  }
}

/**
 * Generate a unique SKU for a book
 */
export function generateBookSKU(title: string, existingSKUs: string[] = []): string {
  // Create base SKU from title
  const titlePart = title
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .split(' ')
    .slice(0, 2)
    .map(word => word.substring(0, 3).toUpperCase())
    .join('')
    .padEnd(4, 'X')
    .substring(0, 4)
  
  // Add timestamp component for uniqueness
  const timestamp = Date.now().toString().slice(-4)
  
  // Random component
  const random = Math.floor(Math.random() * 100).toString().padStart(2, '0')
  
  let sku = `BK-${titlePart}-${timestamp}${random}`
  
  // Ensure uniqueness
  let counter = 1
  while (existingSKUs.includes(sku)) {
    sku = `BK-${titlePart}-${timestamp}${(parseInt(random) + counter).toString().padStart(2, '0')}`
    counter++
  }
  
  return sku
}

/**
 * Validate ISBN format
 */
export function isValidISBN(isbn: string): boolean {
  const cleanISBN = isbn.replace(/[-\s]/g, '')
  
  // ISBN-10 or ISBN-13
  if (cleanISBN.length !== 10 && cleanISBN.length !== 13) {
    return false
  }
  
  // Check if all characters are digits (ISBN-13) or digits + X (ISBN-10)
  if (cleanISBN.length === 10) {
    return /^\d{9}[\dXx]$/.test(cleanISBN)
  }
  
  return /^\d{13}$/.test(cleanISBN)
}
